import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Divider } from '@mui/material';
import Tab from 'components/Common/Tab';
import TextInput from 'components/Common/TextInput';
import Table from 'components/Common/StyledTable';
import { useNavigate } from 'react-router-dom';
import { Card } from '@mui/material';
import routes from 'constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'redux/store';
import { getStaffList } from 'redux/slices/staff/staffSlice';
import CustomButton from 'components/Common/CustomButton';
import { Stack } from '@mui/material';
import { ACTIVE_STAFF, INACTIVE_STAFF, StaffTabs } from 'types/staff';
import { setStaffTab, updateTableState } from 'redux/slices/common/appSlice';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});

const columns = [
    { id: 'name', label: 'Name' },
    { id: 'username', label: 'Username' },
    { id: 'title', label: 'Access Level' },
];

const Staff: React.FC = () => {
    const navigation = useNavigate();
    const [searchValue, setSearchValue] = useState('');
    const { activeRecords, deletedRecords, loading } = useSelector((state: RootState) => state.staff?.staffList);
    const tableState = useSelector((state: RootState) => state.app.table);
    const currentTab = useSelector((state: RootState) => state.app.staff?.currentTab);
    const dispatch = useDispatch<typeof store.dispatch>();

    const staffList = currentTab === ACTIVE_STAFF ? activeRecords : deletedRecords;
    const setTabValue = (value: StaffTabs) => {
        dispatch(setStaffTab(value));
    };
    useEffect(() => {
        const setStatus = currentTab;
        dispatch(getStaffList({ setStatus }));
    }, [currentTab, dispatch]);

    const getSearchData = (data: any, value: string) => {
        return data.filter((item: any) => item.name.toLowerCase().includes(value.toLowerCase()));
    };

    const getData = useMemo(() => {
        let data = staffList;
        if (searchValue) {
            dispatch(updateTableState({ ...tableState, pageNumber: 0 }));
            data = getSearchData(staffList, searchValue);
        }
        return data;
    }, [staffList, searchValue]); //eslint-disable-line

    const navigationHandler = (row: any) => {
        navigation(routes.user.userDetail.replace(':username', row.username), { state: { type: 'employee', status: currentTab } });
    };

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <StyledActionContainer>
                <StyledInnerContainer>
                    <Card sx={{ mb: 0.5 }}>
                        <div>
                            <Tab
                                tabs={[
                                    { value: ACTIVE_STAFF, label: 'Active Staff' },
                                    { value: INACTIVE_STAFF, label: 'Inactive Staff' },
                                ]}
                                tabValue={currentTab}
                                setTabValue={setTabValue}
                            />
                            <Divider />
                            <Stack
                                spacing={26}
                                direction={'row'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                margin=" 25px 70px 20px 20px "
                            >
                                <TextInput width={'35%'} value={searchValue} onChange={(e: any) => setSearchValue(e.target.value)} />

                                {currentTab === ACTIVE_STAFF && (
                                    <CustomButton
                                        title="Invite New"
                                        onClick={() => navigation(routes.staff.add)}
                                        sx={{ mr: 3, width: '40%', fontSize: 28 }}
                                    />
                                )}
                            </Stack>
                        </div>
                    </Card>

                    <Table
                        unStyled
                        navigationHandler={navigationHandler}
                        columns={columns}
                        data={getData}
                        totalCount={getData?.length}
                        loading={loading}
                    />
                </StyledInnerContainer>
            </StyledActionContainer>
        </StyledContainer>
    );
};

export default Staff;
